import {ALERT_DIALOG} from "../libraries/alert-dialog";

const DISCARD_CONFIRM_MESSAGE = "Discard your unsaved changes?"

/**
 * JSON.stringify replacer for the unsaved-changes check. It drops what changes WITHOUT an edit:
 * WikiConfigView re-saves its Editor.js output every 2 seconds (each save stamps a new `time`) and
 * flags a translation `...Manual` whether or not anyone touched it. Only the blocks are compared.
 * Also, the visibility editor writes a rule with no conditions into `visibilityRules` as soon as
 * any new key lands on the data. That is the same as no rule, so it is left out too.
 */
function ignoreVolatileKeys(key, value) {
    if (key === "visibilityRules" && Array.isArray(value)) {
        const hasConditions = value.some(rule => rule && Array.isArray(rule.conditions) && rule.conditions.length > 0)
        return hasConditions ? value : undefined
    }

    if (key.indexOf("wikiContent") !== 0) {
        return value
    }

    if (key.slice(-6) === "Manual") {
        return undefined
    }

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value)
            if (parsed && Array.isArray(parsed.blocks)) {
                return parsed.blocks.map(block => ({type: block.type, data: block.data}))
            }
        } catch (e) {
            // not Editor.js JSON, compare the raw string
        }
    }

    return value
}

const SIDEBAR_BODY_MIXIN = {
    props: {
        /**
         * Data Object that you will use for the Component inner Sidebar body
         * Normally, you need to create your own `data` field and emit it later.
         */
        dataPackage: Object,

        /**
         * Main Form-Data from the Parent.
         * There might be some Configuration need these data.
         */
        formData: Object
    },

    data: () => ({
        /**
         * Ideally, you have put override this key. It will be used to access your data to send it back to your component
         * @example For SidebarFormConfiguration. I created `formConfiguration` data field. And I will handle all data-changing stuff
         * only in `formConfiguration`. And that will be the data I need to send it back to the right component.
         * @required
         */
        dataKey: "",
    }),

    /**
     * Take the "nothing changed yet" baseline after the next tick, so whatever created() wrote into
     * the data (e.g. SidebarControlConfiguration sets `highlightForAlert`) is part of it.
     */
    mounted() {
        this.$nextTick(() => this.markClean())
    },

    methods: {
        /**
         * Close the sidebar without fire any events
         */
        close() {
            this.$emit(this.emitCloseKey, false)
        },

        /**
         * Close from the header X, the bottom Close button or Esc.
         * Asks first if the data changed since the sidebar opened (or since the last Save).
         * A body that mirrors edits into the live form while you type can define `discardChanges()`
         * to put them back once the discard is confirmed.
         */
        requestClose() {
            if (this.isDirty()) {
                if (!window.confirm(DISCARD_CONFIRM_MESSAGE)) {
                    return
                }

                if (typeof this.discardChanges === "function") {
                    this.discardChanges()
                }
            }

            this.close()
        },

        /**
         * Remember the current data as "saved"
         */
        markClean() {
            this._cleanSnapshot = this.serializeData()
        },

        /**
         * Has the data changed since the last markClean()?
         * @returns {boolean}
         */
        isDirty() {
            // no baseline yet (the sidebar was only just mounted) => nothing to lose
            if (this._cleanSnapshot === undefined) {
                return false
            }

            return this.serializeData() !== this._cleanSnapshot
        },

        /**
         * @returns {Object|null} the data as it was at the last markClean() (for discardChanges() to restore from)
         */
        cleanData() {
            try {
                return this._cleanSnapshot ? JSON.parse(this._cleanSnapshot) : null
            } catch (e) {
                return null
            }
        },

        /**
         * @returns {string|null} the data under `dataKey` as comparable text, null if it can't be serialized
         */
        serializeData() {
            try {
                const data = this[this.dataKey]
                return JSON.stringify(data === undefined ? null : data, ignoreVolatileKeys)
            } catch (e) {
                return null
            }
        },

        /**
         * Save the configuration (Actually I will close the sidebar and emit event =)) )
         */
        save(close = false) {
            let data = this[this.dataKey]

            // pre-validation?
            if (this.preSaveValidation) {
                const validationErrorMessage = this.preSaveValidation();

                // if has validation error => show error text and stop the submit process
                if (validationErrorMessage) {
                    ALERT_DIALOG.show(validationErrorMessage);
                    return;
                }
            }

            if (close) {
                this.$emit(this.emitSaveAndCloseKey, data)
            } else {
                this.$emit(this.emitSaveKey, data)
                // the panel stays open: later edits are measured from this save
                this.markClean()
            }
        },
    },

    computed: {
        /**
         * Emit-Key to parent to close the sidebar
         * @returns {string}
         */
        emitCloseKey() {
            return 'close'
        },

        /**
         * Emit-Key to the parent to save the result
         * @returns {string}
         */
        emitSaveKey() {
            return 'save'
        },

        /**
         * Emit-Key - Save and Close
         * @returns {string}
         */
        emitSaveAndCloseKey() {
            return 'saveAndClose'
        }
    }
}

export {
    SIDEBAR_BODY_MIXIN
}